import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  assignScannedSerial, decideScanMatch, type ApplianceMatch, type ScanMatchDecision,
} from "@/lib/scanNavigation";
import type { OCRResult } from "@/components/CameraScanner";

type Pending = Extract<ScanMatchDecision, { kind: "confirm-no-serial" | "choose" }>;

interface Props {
  /** Decision needing technician input; null = closed. */
  decision: Pending | null;
  ocr: OCRResult | null;
  /** All active-company candidates, used to re-decide on a typed serial. */
  candidates: ApplianceMatch[];
  /** Open an existing appliance; `serial` = a serial typed in the dialog, if any. */
  onOpen: (applianceId: string) => void;
  /** Treat as a new unit; `serial` = a serial typed in the dialog, if any. */
  onNew: (serial?: string) => void;
}

/** FIX-304: never open a same-model appliance silently; ask the technician. */
export function ScanMatchDialog({ decision, ocr, candidates, onOpen, onNew }: Props) {
  const { t } = useTranslation("common");
  const [current, setCurrent] = useState<Pending | null>(decision);
  const [typed, setTyped] = useState("");
  const [typedSerial, setTypedSerial] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCurrent(decision);
    setTyped("");
    setTypedSerial(undefined);
  }, [decision]);

  const label = (a: ApplianceMatch) => a.name || [a.brand, a.model_number].filter(Boolean).join(" ") || a.id.slice(0, 8);

  const confirmSame = async (a: ApplianceMatch, serial: string) => {
    setBusy(true);
    const ok = await assignScannedSerial(a.id, serial);
    setBusy(false);
    if (ok) onOpen(a.id);
    else {
      toast.error(t("scanMatch.serialTaken"));
      onNew(serial);
    }
  };

  const applySerial = () => {
    if (!ocr || !typed.trim()) return;
    const serial = typed.trim();
    const next = decideScanMatch({ ...ocr, serialNumber: serial }, candidates);
    setTypedSerial(serial);
    if (next.kind === "open") onOpen(next.appliance.id);
    else if (next.kind === "new") onNew(serial);
    else setCurrent(next);
  };

  const pick = (a: ApplianceMatch) => {
    if (typedSerial && !a.serial_number) void confirmSame(a, typedSerial);
    else onOpen(a.id);
  };

  return (
    <Dialog open={!!current} onOpenChange={(o) => { if (!o && !busy) onNew(typedSerial); }}>
      <DialogContent>
        {current?.kind === "confirm-no-serial" ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("scanMatch.confirmTitle")}</DialogTitle>
              <DialogDescription>{t("scanMatch.confirmBody", { name: label(current.appliance) })}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" disabled={busy} onClick={() => onNew(current.serial)}>{t("scanMatch.no")}</Button>
              <Button disabled={busy} onClick={() => confirmSame(current.appliance, current.serial)}>{t("scanMatch.yes")}</Button>
            </DialogFooter>
          </>
        ) : current ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("scanMatch.chooseTitle")}</DialogTitle>
              <DialogDescription>{t("scanMatch.chooseBody")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {current.candidates.map((a) => (
                <Button key={a.id} variant="outline" className="w-full justify-start" disabled={busy} onClick={() => pick(a)}>
                  {label(a)} · S/N {a.serial_number || t("scanMatch.none")}
                </Button>
              ))}
            </div>
            {!typedSerial && (
              <div className="space-y-1">
                <Label htmlFor="scan-match-serial">{t("scanMatch.serialLabel")}</Label>
                <div className="flex gap-2">
                  <Input id="scan-match-serial" value={typed} onChange={(e) => setTyped(e.target.value)} />
                  <Button variant="secondary" onClick={applySerial} disabled={!typed.trim()}>{t("scanMatch.apply")}</Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" disabled={busy} onClick={() => onNew(typedSerial)}>{t("scanMatch.newAppliance")}</Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
